import { useCallback, useEffect, useState } from "react";
import { MessageSquare, Check, Trash2, Quote, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function useSelectionIn(containerId) {
  const [sel, setSel] = useState("");
  useEffect(() => {
    const onUp = () => {
      const s = window.getSelection();
      const el = document.getElementById(containerId);
      if (s && el && s.rangeCount && el.contains(s.anchorNode) && s.toString().trim()) setSel(s.toString().trim().slice(0, 1000));
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, [containerId]);
  return [sel, setSel];
}

export function CommentsPanel({ proposalId, containerId }) {
  const { user, hasPkg } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [quote, setQuote] = useSelectionIn(containerId);
  const canComment = ["admin", "pro"].includes(user?.role) || hasPkg("suite");
  const load = useCallback(() => api.get(`/proposals/${proposalId}/comments`).then((r) => setItems(r.data)), [proposalId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await api.post(`/proposals/${proposalId}/comments`, { text, quote });
      setText("");
      setQuote("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const resolve = async (c) => { await api.patch(`/proposals/${proposalId}/comments/${c.comment_id}`, { resolved: !c.resolved }); load(); };
  const del = async (c) => { await api.delete(`/proposals/${proposalId}/comments/${c.comment_id}`); load(); };
  const jump = (q) => { if (q && window.find) window.find(q.slice(0, 80)); };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="comments-panel">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-emerald-600" />Comments <span className="text-xs text-slate-400">({items.filter((c) => !c.resolved).length} open)</span></h3>
      {canComment ? (
        <div className="mt-3">
          {quote ? (
            <div className="text-xs bg-amber-50 border-l-2 border-amber-400 px-2 py-1.5 mb-2 text-slate-600 flex gap-2" data-testid="comment-selected-quote">
              <Quote className="h-3 w-3 shrink-0 mt-0.5" /><span className="line-clamp-3 flex-1">{quote}</span>
              <button onClick={() => setQuote("")} className="text-slate-400 hover:text-slate-700">×</button>
            </div>
          ) : <p className="text-xs text-slate-400 mb-2">Tip: highlight text in the document to comment on it.</p>}
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" data-testid="comment-input" />
          <Button size="sm" onClick={add} disabled={!text.trim()} className="mt-2 bg-slate-900 hover:bg-slate-800" data-testid="comment-submit-button">Comment</Button>
        </div>
      ) : (
        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Lock className="h-3 w-3" />Team comments are part of the Full Verify Suite.</p>
      )}
      <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto">
        {items.map((c) => (
          <div key={c.comment_id} className={`border rounded-md p-3 text-sm ${c.resolved ? "opacity-50 border-slate-100" : c.author_role === "editor" ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid={`comment-${c.comment_id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-900 text-xs">{c.author_name}{c.author_role === "editor" && <span className="ml-1.5 text-[10px] font-mono uppercase text-amber-700">Certified editor</span>}</span>
              <span className="flex gap-1">
                <button onClick={() => resolve(c)} title={c.resolved ? "Reopen" : "Resolve"} className="p-1 text-slate-400 hover:text-emerald-600" data-testid={`comment-resolve-${c.comment_id}`}><Check className="h-3.5 w-3.5" /></button>
                {c.author_id === user?.user_id && <button onClick={() => del(c)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`comment-delete-${c.comment_id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </span>
            </div>
            {c.quote && <button onClick={() => jump(c.quote)} className="block text-left text-xs text-slate-500 border-l-2 border-amber-400 pl-2 my-1.5 line-clamp-2 hover:text-slate-800">"{c.quote}"</button>}
            <p className="text-slate-700 whitespace-pre-wrap">{c.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
